import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Build as BuildIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Bay {
  id: string;
  name: string;
  type: 'general' | 'diagnostic' | 'bodywork' | 'electrical' | 'mechanical';
}

interface Technician {
  id: string;
  name: string;
  specialization: 'general' | 'diagnostic' | 'bodywork' | 'electrical' | 'mechanical';
}

interface Availability {
  bayId: string;
  date: Date;
  slots: {
    time: string;
    isAvailable: boolean;
    appointmentId?: string;
    appointmentTitle?: string;
    customerName?: string;
    vehicleInfo?: string;
  }[];
}

export const AvailabilityCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedBay, setSelectedBay] = useState<string>('all');
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');

  // Mock data
  const bays: Bay[] = [
    { id: '1', name: 'Bay 1', type: 'general' },
    { id: '2', name: 'Bay 2', type: 'diagnostic' },
    { id: '3', name: 'Bay 3', type: 'mechanical' },
    { id: '4', name: 'Bay 4', type: 'bodywork' },
  ];

  const technicians: Technician[] = [
    { id: '1', name: 'John Smith', specialization: 'general' },
    { id: '2', name: 'Sarah Johnson', specialization: 'diagnostic' },
    { id: '3', name: 'Mike Williams', specialization: 'bodywork' },
    { id: '4', name: 'David Brown', specialization: 'mechanical' },
  ];

  // Generate mock availability data
  const generateAvailabilityData = (): Availability[] => {
    const availabilityData: Availability[] = [];

    bays.forEach(bay => {
      const slots = [];
      for (let hour = 8; hour < 18; hour++) {
        const hourString = hour.toString().padStart(2, '0');
        
        // Morning slot
        slots.push({
          time: `${hourString}:00`,
          isAvailable: Math.random() > 0.3, // 70% chance of being available
          ...(Math.random() > 0.7 && {
            appointmentId: `APT-${Math.floor(Math.random() * 1000)}`,
            appointmentTitle: ['Oil Change', 'Brake Inspection', 'Tire Rotation', 'Engine Diagnostic'][Math.floor(Math.random() * 4)],
            customerName: ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis'][Math.floor(Math.random() * 4)],
            vehicleInfo: ['Toyota Corolla 2019', 'Honda Civic 2020', 'BMW X5 2021', 'Ford F-150 2018'][Math.floor(Math.random() * 4)],
          }),
        });
        
        // Afternoon slot
        slots.push({
          time: `${hourString}:30`,
          isAvailable: Math.random() > 0.3, // 70% chance of being available
          ...(Math.random() > 0.7 && {
            appointmentId: `APT-${Math.floor(Math.random() * 1000)}`,
            appointmentTitle: ['Oil Change', 'Brake Inspection', 'Tire Rotation', 'Engine Diagnostic'][Math.floor(Math.random() * 4)],
            customerName: ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis'][Math.floor(Math.random() * 4)],
            vehicleInfo: ['Toyota Corolla 2019', 'Honda Civic 2020', 'BMW X5 2021', 'Ford F-150 2018'][Math.floor(Math.random() * 4)],
          }),
        });
      }

      availabilityData.push({
        bayId: bay.id,
        date: currentDate,
        slots,
      });
    });

    return availabilityData;
  };

  const availabilityData = generateAvailabilityData();

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const filteredAvailability = availabilityData.filter(
    availability => selectedBay === 'all' || availability.bayId === selectedBay
  );

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Availability Calendar
        </Typography>

        {/* Calendar Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handlePreviousDay}>
            <ChevronLeftIcon />
          </IconButton>
          <Button startIcon={<TodayIcon />} onClick={handleToday} sx={{ mx: 1 }}>
            Today
          </Button>
          <IconButton onClick={handleNextDay}>
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
            {formatDate(currentDate)}
          </Typography>
          <FormControl sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel>Bay</InputLabel>
            <Select
              value={selectedBay}
              label="Bay"
              onChange={(e) => setSelectedBay(e.target.value as string)}
              size="small"
            >
              <MenuItem value="all">All Bays</MenuItem>
              {bays.map((bay) => (
                <MenuItem key={bay.id} value={bay.id}>
                  {bay.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel>Technician</InputLabel>
            <Select
              value={selectedTechnician}
              label="Technician"
              onChange={(e) => setSelectedTechnician(e.target.value as string)}
              size="small"
            >
              <MenuItem value="all">All Technicians</MenuItem>
              {technicians.map((tech) => (
                <MenuItem key={tech.id} value={tech.id}>
                  {tech.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />}>
            Block Time
          </Button>
        </Box>

        {/* Availability Grid */}
        <Grid container spacing={3}>
          {filteredAvailability.map((availability) => {
            const bay = bays.find((b) => b.id === availability.bayId);
            
            return (
              <Grid item xs={12} key={availability.bayId}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {bay ? `${bay.name} (${bay.type.charAt(0).toUpperCase() + bay.type.slice(1)})` : 'Unknown Bay'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={1}>
                    {availability.slots.map((slot, index) => (
                      <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                        <Card
                          sx={{
                            bgcolor: slot.isAvailable
                              ? slot.appointmentId
                                ? 'primary.light'
                                : 'success.light'
                              : 'error.light',
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 },
                          }}
                        >
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              {slot.time}
                            </Typography>
                            
                            {slot.appointmentId ? (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {slot.appointmentTitle}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                                  <Typography variant="caption">
                                    {slot.customerName}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <CarIcon fontSize="small" sx={{ mr: 0.5 }} />
                                  <Typography variant="caption">
                                    {slot.vehicleInfo}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <Chip
                                label={slot.isAvailable ? 'Available' : 'Unavailable'}
                                size="small"
                                color={slot.isAvailable ? 'success' : 'error'}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Legend */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: 'success.light',
                borderRadius: 1,
                mr: 1,
              }}
            />
            <Typography variant="body2">Available</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: 'primary.light',
                borderRadius: 1,
                mr: 1,
              }}
            />
            <Typography variant="body2">Booked</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: 'error.light',
                borderRadius: 1,
                mr: 1,
              }}
            />
            <Typography variant="body2">Unavailable</Typography>
          </Box>
        </Box>
      </Box>
    </DashboardLayout>
  );
}; 